import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';

import { queryClient } from '@/lib/queryClient';
import { useAuth } from '@/hooks/useAuth';
import { Layout } from '@/components/layout';
import { LoadingSpinner } from '@/components/common';

// Lazy loading des pages
const Dashboard = React.lazy(() => import('@/pages/Dashboard').then(m => ({ default: m.Dashboard })));
const PendingAMV = React.lazy(() => import('@/pages/PendingAMV').then(m => ({ default: m.PendingAMV })));
const MechanicDetail = React.lazy(() => import('@/pages/MechanicDetail').then(m => ({ default: m.MechanicDetail })));
const AMVList = React.lazy(() => import('@/pages/AMVList').then(m => ({ default: m.AMVList })));
const AMVEditor = React.lazy(() => import('@/pages/AMVEditor').then(m => ({ default: m.AMVEditor })));

// Composant de protection des routes
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Debug logs
  React.useEffect(() => {
    console.log('ProtectedRoute - isLoading:', isLoading, 'isAuthenticated:', isAuthenticated, 'user:', !!user);
  }, [isLoading, isAuthenticated, user]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
        <p className="ml-4 text-gray-600">Chargement...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('User not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('User authenticated, showing protected content');
  return <Layout>{children}</Layout>;
};

// Import du composant de dev
import { DevLogin } from '@/components/DevLogin';

// Page de login avec mode développement
const LoginPage: React.FC = () => {
  const { signIn, isLoading } = useAuth();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [devMode, setDevMode] = React.useState(false);

  // Mode développement temporaire
  const handleDevLogin = () => {
    // Simuler un utilisateur connecté pour le développement
    window.localStorage.setItem('dev-user', JSON.stringify({
      id: 'dev-user-id',
      email: 'admin@gomecano.com',
      role: 'admin'
    }));
    window.location.reload();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await signIn(email, password);
  };

  // Mode développement désactivé - utiliser l'authentification Supabase
  // if (import.meta.env.DEV) {
  //   return <DevLogin onLogin={handleDevLogin} />;
  // }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            AMV Gomecano
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Connectez-vous à votre compte
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Mot de passe
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {isLoading ? 'Connexion...' : 'Se connecter'}
            </button>
          </div>
        </form>
        
        <div className="text-center">
          <button
            onClick={() => setDevMode(true)}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Mode développement
          </button>
          {devMode && (
            <div className="mt-4">
              <DevLogin onLogin={handleDevLogin} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <div className="App">
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <Dashboard />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/pending" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <PendingAMV />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/mechanic/:matricule" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <MechanicDetail />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/amv" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <AMVList />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/amv/:id" element={
              <ProtectedRoute>
                <Suspense fallback={<LoadingSpinner />}>
                  <AMVEditor />
                </Suspense>
              </ProtectedRoute>
            } />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          
          {/* Toast notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10B981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#fff',
                },
              },
            }}
          />
        </div>
      </Router>
      
      {/* React Query DevTools */}
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
